import React, { useState } from 'react';
import { UserPlus, UserCog } from 'lucide-react';
import Modal from './Modal';
import CampoTelefono from './CampoTelefono';
import CampoContrasena, { RequisitosContrasena, contrasenaValida } from './CampoContrasena';
import { PAIS_PRINCIPAL, numeroInternacional, telefonoDesdeInternacional } from './paises';

const claseInput = 'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-500';

// Crear o editar un usuario de un cliente de Punto V. Al editar, la
// contraseña es opcional y el correo no cambia (es el usuario de login). El
// dueño de la empresa conserva su rol y no se puede desactivar.
const FormularioUsuarioPuntoV = ({ cliente, usuario, roles, onGuardar, onCancel }) => {
  const editando = Boolean(usuario);
  const esDueno = Boolean(usuario?.es_dueno);
  const [form, setForm] = useState({
    nombre_completo: usuario?.nombre_completo || '',
    correo: usuario?.correo || '',
    id_rol: usuario?.id_rol ? String(usuario.id_rol) : '',
    activo: usuario ? usuario.activo : true
  });
  const [telefono, setTelefono] = useState(
    usuario ? telefonoDesdeInternacional(usuario.origin_number) : { iso: PAIS_PRINCIPAL, numero: '' }
  );
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [guardando, setGuardando] = useState(false);

  const cambiar = (campo, valor) => setForm(prev => ({ ...prev, [campo]: valor }));
  // Un rol inactivo no se ofrece, salvo que sea el que el usuario ya tiene.
  const opcionesRol = roles.filter(r => r.activo || String(r.id) === form.id_rol);

  const enviar = async (e) => {
    e.preventDefault();
    setError('');
    if ((!editando || password) && !contrasenaValida(password)) {
      setError('La contraseña no cumple los requisitos indicados debajo del campo.');
      return;
    }
    const origin = numeroInternacional(telefono);
    if (origin.length < 10 || origin.length > 15) {
      setError('El teléfono debe tener entre 10 y 15 dígitos contando el código de país.');
      return;
    }
    setGuardando(true);
    try {
      await onGuardar({
        nombre_completo: form.nombre_completo.trim(),
        ...(editando ? {} : { correo: form.correo.trim() }),
        origin_number: origin,
        id_rol: Number(form.id_rol),
        activo: form.activo,
        ...(password ? { password } : {})
      });
    } catch (err) {
      setError(err.message || 'No se pudo guardar el usuario');
      setGuardando(false);
    }
  };

  return (
    <Modal
      titulo={editando ? 'Editar usuario' : 'Nuevo usuario'}
      subtitulo={editando ? `${usuario.correo} · ${cliente.base_datos}` : cliente.base_datos}
      icono={editando ? UserCog : UserPlus}
      onClose={onCancel}
      bloqueado={guardando}
      ancho="lg"
      pie={(
        <>
          <button type="button" onClick={onCancel} disabled={guardando} className="px-4 py-2 text-sm text-gray-700 rounded-lg hover:bg-gray-100">
            Cancelar
          </button>
          <button
            type="submit"
            form="form-usuario-puntov"
            disabled={guardando}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium disabled:opacity-50"
          >
            {guardando ? 'Guardando…' : editando ? 'Guardar cambios' : 'Crear usuario'}
          </button>
        </>
      )}
    >
      <form id="form-usuario-puntov" onSubmit={enviar} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label htmlFor="usuario-puntov-nombre" className="block text-xs font-medium text-gray-700 mb-1">Nombre completo *</label>
            <input
              id="usuario-puntov-nombre"
              value={form.nombre_completo}
              onChange={(e) => cambiar('nombre_completo', e.target.value)}
              required
              minLength={2}
              maxLength={50}
              className={claseInput}
            />
          </div>
          <div>
            <label htmlFor="usuario-puntov-correo" className="block text-xs font-medium text-gray-700 mb-1">Correo *</label>
            <input
              id="usuario-puntov-correo"
              type="email"
              value={form.correo}
              onChange={(e) => cambiar('correo', e.target.value)}
              required
              disabled={editando}
              maxLength={75}
              autoComplete="off"
              className={claseInput}
            />
            {editando && <p className="text-xs text-gray-400 mt-1">El correo es el usuario de acceso: no se cambia.</p>}
          </div>
          <div>
            <span className="block text-xs font-medium text-gray-700 mb-1">Teléfono / origen *</span>
            <CampoTelefono value={telefono} onChange={setTelefono} required />
          </div>
          <div>
            <label htmlFor="usuario-puntov-rol" className="block text-xs font-medium text-gray-700 mb-1">Rol *</label>
            <select
              id="usuario-puntov-rol"
              value={form.id_rol}
              onChange={(e) => cambiar('id_rol', e.target.value)}
              required
              disabled={esDueno}
              className={claseInput}
            >
              <option value="">Seleccionar rol</option>
              {opcionesRol.map(r => (
                <option key={r.id} value={String(r.id)}>{r.nombre}{r.activo ? '' : ' (inactivo)'}</option>
              ))}
            </select>
            {esDueno && <p className="text-xs text-gray-400 mt-1">Es el dueño de la empresa: conserva su rol.</p>}
            {!esDueno && opcionesRol.length === 0 && (
              <p className="text-xs text-amber-600 mt-1">Este cliente no tiene roles activos. Cree uno en «Roles y permisos».</p>
            )}
          </div>
          <div>
            <label htmlFor="usuario-puntov-password" className="block text-xs font-medium text-gray-700 mb-1">
              {editando ? 'Nueva contraseña' : 'Contraseña *'}
            </label>
            <CampoContrasena
              id="usuario-puntov-password"
              value={password}
              onChange={setPassword}
              invalido={password !== '' && !contrasenaValida(password)}
              required={!editando}
            />
            {editando && !password
              ? <p className="text-xs text-gray-400 mt-1">Déjela vacía para conservar la actual.</p>
              : <RequisitosContrasena value={password} />}
          </div>
        </div>

        <label
          htmlFor="usuario-puntov-activo"
          className={`flex items-center justify-between gap-4 rounded-xl border border-gray-200 p-4 ${esDueno ? 'opacity-60' : 'cursor-pointer'}`}
        >
          <span>
            <span className="block text-sm font-medium text-gray-800">Estado del usuario</span>
            <span className="block text-xs text-gray-500">
              {esDueno
                ? 'El dueño de la empresa no se puede desactivar.'
                : form.activo ? 'Activo: puede iniciar sesión en Punto V Manager.' : 'Suspendido: no puede iniciar sesión.'}
            </span>
          </span>
          <input
            id="usuario-puntov-activo"
            type="checkbox"
            role="switch"
            checked={form.activo}
            onChange={(e) => cambiar('activo', e.target.checked)}
            disabled={esDueno}
            className="sr-only peer"
          />
          <span
            aria-hidden="true"
            className="relative w-11 h-6 flex-shrink-0 rounded-full bg-gray-300 transition-colors peer-checked:bg-blue-600 peer-focus-visible:ring-2 peer-focus-visible:ring-blue-400 after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:w-5 after:h-5 after:rounded-full after:bg-white after:shadow after:transition-transform peer-checked:after:translate-x-5"
          />
        </label>

        {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">{error}</div>}
      </form>
    </Modal>
  );
};

export default FormularioUsuarioPuntoV;
